import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { getStudent, getStudents } from '../../services/studentService';
import useLiveJobs from '../../hooks/useLiveJobs';
import { getDashboardPath } from '../../App';
import ProfileModal from './ProfileModal';
import ProfileAvatar from './ProfileAvatar';
import NotificationModal from './NotificationModal';
import NavIcon from './NavIcon';
import { initials } from './Sidebar';
import logoImg from '../../static/images/logo.png';

const STUDENT_PAGES = {
  information: 'Compliance report',
  files: 'File cabinet',
  jobs: 'Uploads & jobs',
  edit: 'Edit',
  'iep-checklist': 'IEP checklist',
  eligibility_checklist: 'Eligibility checklist',
};
const SECTION_NAMES = {
  identity_timeline: 'Identity & timeline',
  evaluation: 'Evaluation',
  present_levels: 'Present levels',
  goals: 'Goals',
  services_lre: 'Services & LRE',
  accommodations: 'Accommodations',
  assessment_participation: 'Assessment participation',
  behavior: 'Behavior',
  transition_plan: 'Transition plan',
  iep_compliance: 'IEP compliance',
  eligibility_compliance: 'Eligibility compliance',
};

// Student names for breadcrumbs, fetched once per id.
const studentNames = new Map();

function useStudentName(id) {
  const [name, setName] = useState(() => (id ? studentNames.get(id) : null));
  useEffect(() => {
    if (!id) { setName(null); return undefined; }
    if (studentNames.has(id)) { setName(studentNames.get(id)); return undefined; }
    let alive = true;
    getStudent(id)
      .then(({ data }) => {
        const n = data?.data?.student_name;
        if (n) studentNames.set(id, n);
        if (alive) setName(n || null);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [id]);
  return name;
}

function useBreadcrumbs(role) {
  const { pathname } = useLocation();
  const parts = pathname.split('/').filter(Boolean);
  const studentId = parts[0] === 'students' && /^\d+$/.test(parts[1] || '') ? parts[1] : null;
  const studentName = useStudentName(studentId);
  const crumbs = [];
  const home = getDashboardPath(role);

  if (pathname === home || parts[0]?.endsWith('-dashboard')) return [{ label: 'Dashboard' }];
  if (parts[0] === 'pipeline-jobs') return [{ label: 'Pipeline jobs' }];
  if (parts[0] === 'students') {
    crumbs.push({ label: 'Students', to: '/students' });
    if (parts[1] === 'add') crumbs.push({ label: 'Add student' });
    if (studentId) {
      crumbs.push({ label: studentName || 'Student', to: `/students/${studentId}` });
      const page = parts[2];
      if (page) crumbs.push({ label: STUDENT_PAGES[page] || page, to: `/students/${studentId}/${page}` });
      if (page === 'information' && parts[3] === 'section' && parts[4]) {
        crumbs.push({ label: SECTION_NAMES[parts[4]] || parts[4].replace(/_/g, ' ') });
      }
      if (page === 'files' && parts[3] === 'add') crumbs.push({ label: 'Add file' });
    }
  } else if (parts[0] === 'users' || parts[0] === 'schools') {
    const label = parts[0] === 'users' ? 'Users' : 'Schools';
    const one = parts[0] === 'users' ? 'User' : 'School';
    crumbs.push({ label, to: `/${parts[0]}` });
    if (parts[1] === 'add') crumbs.push({ label: `Add ${one.toLowerCase()}` });
    else if (parts[1]) {
      crumbs.push({ label: one, to: `/${parts[0]}/${parts[1]}` });
      if (parts[2] === 'edit') crumbs.push({ label: 'Edit' });
    }
  } else if (parts[0] === 'admin' && parts[1] === 'eligibility-templates') {
    crumbs.push({ label: 'Eligibility templates', to: '/admin/eligibility-templates' });
    if (parts[2]) crumbs.push({ label: 'Template' });
  }
  // The last crumb is the current page: never a link.
  if (crumbs.length) delete crumbs[crumbs.length - 1].to;
  return crumbs;
}

function Breadcrumbs({ crumbs, home }) {
  if (!crumbs.length) return null;
  const atHome = crumbs.length === 1 && crumbs[0].label === 'Dashboard';
  return (
    <nav aria-label="Breadcrumb" className="min-w-0 flex items-center gap-1.5 text-[13px] text-slate-500 dark:text-slate-400">
      {!atHome && (
        <>
          <Link to={home} aria-label="Dashboard" title="Dashboard"
            className="flex-shrink-0 p-1 -m-1 rounded-md hover:text-slate-900 hover:bg-slate-100 dark:hover:text-white dark:hover:bg-slate-800">
            <NavIcon name="home" className="w-4 h-4" />
          </Link>
          <span className="text-slate-300 dark:text-slate-600" aria-hidden="true">/</span>
        </>
      )}
      {crumbs.map((c, i) => (
        <React.Fragment key={`${c.label}-${i}`}>
          {i > 0 && <span className="text-slate-300 dark:text-slate-600" aria-hidden="true">/</span>}
          {c.to ? (
            <Link to={c.to} className="truncate max-w-[12rem] hover:text-slate-900 dark:hover:text-white">{c.label}</Link>
          ) : (
            <span aria-current="page" className="truncate max-w-[16rem] font-semibold text-slate-900 dark:text-white">{c.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

function GlobalSearch({ autoFocus = false, onDone }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || '')
        || document.activeElement?.isContentEditable;
      if ((e.key === 'k' && (e.ctrlKey || e.metaKey)) || (e.key === '/' && !typing)) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setLoading(false); return undefined; }
    setLoading(true);
    const t = setTimeout(() => {
      getStudents({ search: term, page: 1, limit: 6 })
        .then(({ data }) => { setResults(data?.data?.students || []); setActive(0); })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = useCallback((s) => {
    setOpen(false);
    setQ('');
    onDone?.();
    navigate(`/students/${s.id}/information`);
  }, [navigate, onDone]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); onDone?.(); return; }
    if (!results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % results.length); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + results.length) % results.length); }
    if (e.key === 'Enter') { e.preventDefault(); go(results[active]); }
  };

  const showPanel = open && q.trim().length >= 2;

  return (
    <div ref={boxRef} className="relative w-full max-w-[440px]">
      <label className="flex items-center gap-2.5 h-[38px] rounded-[10px] border border-slate-200 bg-slate-50 px-3 text-[13px] text-slate-400 focus-within:border-indigo-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-indigo-500/20 dark:bg-slate-800/60 dark:border-slate-700 dark:focus-within:bg-slate-800">
        <NavIcon name="search" className="w-4 h-4" strokeWidth={2} />
        <span className="sr-only">Search students</span>
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search students by name or ID…"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls="global-search-results"
          aria-autocomplete="list"
          className="flex-1 min-w-0 bg-transparent text-slate-900 placeholder-slate-400 outline-none dark:text-white"
        />
        <kbd className="hidden sm:inline-block rounded-[5px] border border-slate-200 bg-white px-1.5 text-[11px] font-sans text-slate-400 dark:bg-slate-900 dark:border-slate-700">Ctrl K</kbd>
      </label>
      {showPanel && (
        <div id="global-search-results" role="listbox" className="absolute left-0 right-0 top-[44px] z-50 rounded-xl border border-slate-200 bg-white shadow-xl dark:bg-slate-800 dark:border-slate-700 overflow-hidden">
          {loading && <p className="px-4 py-3 text-sm text-slate-500">Searching…</p>}
          {!loading && results.length === 0 && <p className="px-4 py-3 text-sm text-slate-500">No students match “{q.trim()}”.</p>}
          {!loading && results.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(s)}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-left ${i === active ? 'bg-indigo-50 dark:bg-indigo-500/15' : ''}`}
            >
              <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-200 flex items-center justify-center text-[11px] font-bold flex-shrink-0">{initials(s.student_name)}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-slate-900 dark:text-white truncate">{s.student_name}</span>
                <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">
                  #{s.student_id} · Grade {s.grade}{s.school_name ? ` · ${s.school_name}` : ''}
                </span>
              </span>
              <NavIcon name="chevronRight" className="w-4 h-4 text-slate-300" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function IconButton({ label, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="relative w-9 h-9 rounded-[10px] border border-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
    >
      {children}
    </button>
  );
}

export default function Header({ onMenuClick, withSidebar = false, sidebarCollapsed = false }) {
  const { user, logout, unreadNotificationCount, setUnreadNotificationCount } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const crumbs = useBreadcrumbs(user?.role);
  const live = useLiveJobs();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const dropRef = useRef(null);

  const handleUnreadCountChange = useCallback((count) => {
    if (typeof count === 'number') setUnreadNotificationCount(count);
  }, [setUnreadNotificationCount]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
  const offset = withSidebar ? (sidebarCollapsed ? 'lg:left-[68px]' : 'lg:left-[248px]') : '';

  return (
    <header className={`fixed top-0 left-0 right-0 ${offset} h-16 z-40 flex items-center gap-3 sm:gap-3.5 px-4 sm:px-7 bg-white/90 backdrop-blur border-b border-slate-200 dark:bg-slate-900/90 dark:border-slate-800 transition-[left] duration-300`}>
      {onMenuClick && (
        <button
          type="button"
          onClick={onMenuClick}
          className="lg:hidden -ml-1 p-2 rounded-[10px] text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800"
          aria-label="Open menu"
        >
          <NavIcon name="menu" className="w-5 h-5" />
        </button>
      )}
      {/* With the nav, the logo lives there; the header shows it only when the nav is a drawer. */}
      <Link to={getDashboardPath(user?.role)} className={withSidebar && !sidebarCollapsed ? 'lg:hidden flex-shrink-0' : 'flex-shrink-0'} aria-label="Athenian Academy — dashboard">
        <img src={logoImg} alt="Athenian Academy" className="h-9 sm:h-10 w-auto max-w-[168px] object-contain object-left dark:bg-white dark:rounded-lg dark:px-1" />
      </Link>

      <div className={`min-w-0 ${withSidebar ? 'hidden lg:block lg:flex-[1.6] lg:basis-0' : 'hidden md:block flex-[1.6] basis-0'}`}>
        <Breadcrumbs crumbs={crumbs} home={getDashboardPath(user?.role)} />
      </div>

      <div className="hidden md:flex flex-[1.2] justify-center min-w-[200px]">
        <GlobalSearch />
      </div>

      <div className="flex-1 basis-0 flex items-center justify-end gap-2 sm:gap-2.5">
        <span className="md:hidden">
          <IconButton label="Search students" onClick={() => setMobileSearch(true)}>
            <NavIcon name="search" className="w-[18px] h-[18px]" strokeWidth={2} />
          </IconButton>
        </span>
        {(user?.role === 1 || user?.role === 2) && (
          <Link
            to="/pipeline-jobs"
            title={live.intakePaused ? 'New uploads are paused' : 'New uploads are accepted'}
            className={`hidden sm:inline-flex items-center gap-1.5 rounded-full px-[9px] py-0.5 text-[11.5px] font-semibold whitespace-nowrap ${
              live.intakePaused
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300'
                : 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${live.intakePaused ? 'bg-amber-500' : 'bg-green-600'}`} />
            {live.intakePaused ? 'Intake paused' : 'Intake open'}
          </Link>
        )}
        <IconButton label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'} onClick={toggleTheme}>
          <NavIcon name={theme === 'light' ? 'moon' : 'sun'} className="w-[18px] h-[18px]" />
        </IconButton>
        <IconButton
          label={unreadNotificationCount ? `${unreadNotificationCount} unread notifications` : 'Notifications'}
          onClick={() => setNotificationsOpen(true)}
        >
          <NavIcon name="bell" className="w-[18px] h-[18px]" />
          {unreadNotificationCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-600 rounded-full ring-2 ring-white dark:ring-slate-900">
              {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
            </span>
          )}
        </IconButton>
        <NotificationModal
          open={notificationsOpen}
          onClose={() => setNotificationsOpen(false)}
          onUnreadCountChange={handleUnreadCountChange}
        />

        {/* Pages without the nav (compliance report) keep the profile menu up here. */}
        {!withSidebar && (
          <div className="relative" ref={dropRef}>
            <button
              type="button"
              onClick={() => { setDropdownOpen(!dropdownOpen); setShowProfile(false); }}
              className="flex items-center gap-2 pl-1.5 pr-2 sm:pr-2.5 h-9 rounded-[10px] border border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
              aria-haspopup="menu"
              aria-expanded={dropdownOpen}
            >
              <span id="header-profile-avatar" className="w-7 h-7 rounded-lg overflow-hidden flex-shrink-0">
                <ProfileAvatar type="user" id={user?.id} variant="solid" hasProfileImage={user?.has_profile_image}
                  containerClassName="w-full h-full" className="w-full h-full object-cover block" />
              </span>
              <span className="text-slate-700 dark:text-slate-300 text-sm font-medium hidden sm:block max-w-24 truncate">{user?.first_name}</span>
              <NavIcon name="chevronDown" className="w-4 h-4 text-slate-400 hidden sm:block" />
            </button>
            {dropdownOpen && !showProfile && (
              <div role="menu" className="absolute right-0 top-12 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{fullName}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{user?.email}</p>
                </div>
                <button type="button" role="menuitem" onClick={() => { setShowProfile(true); setDropdownOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700">
                  <NavIcon name="user" className="w-4 h-4" /> View profile
                </button>
                <button type="button" role="menuitem" onClick={() => { logout(); navigate('/login'); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 border-t border-slate-100 dark:border-slate-700">
                  <NavIcon name="logout" className="w-4 h-4" /> Log out
                </button>
              </div>
            )}
            {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}
          </div>
        )}
      </div>

      {mobileSearch && (
        <div className="md:hidden absolute inset-0 z-40 flex items-center gap-2 px-3 bg-white dark:bg-slate-900">
          <GlobalSearch autoFocus onDone={() => setMobileSearch(false)} />
          <button type="button" onClick={() => setMobileSearch(false)} className="p-2 text-slate-500" aria-label="Close search">
            <NavIcon name="close" className="w-5 h-5" />
          </button>
        </div>
      )}
    </header>
  );
}
